import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../config/api.js';

// ---- simple cache (persists for the session) ----
const CALLER_CACHE = new Map();
const cacheKey = (sig) => `callerInfo:${sig}`;
function readCache(sig) {
  if (!sig) return null;
  if (CALLER_CACHE.has(sig)) return CALLER_CACHE.get(sig);
  try {
    const raw = sessionStorage.getItem(cacheKey(sig));
    if (raw) {
      const data = JSON.parse(raw);
      CALLER_CACHE.set(sig, data);
      return data;
    }
  } catch {}
  return null;
}
function writeCache(sig, data) {
  if (!sig) return;
  CALLER_CACHE.set(sig, data);
  try { sessionStorage.setItem(cacheKey(sig), JSON.stringify(data)); } catch {}
}

// helpers
const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');

// resolve value from multiple possible keys (first non-empty wins)
const getFromRow = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return '';
};

export default function CallerInformationTab({ row }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState(null);

  // derive a stable signature from the row:
  // cli (digits only) + callerId
  const signature = useMemo(() => {
    if (!row) return '';
    const phone = getFromRow(
      row,
      'contactNumber', 'ContactNumber',
      'PhoneNo', 'phoneNo',
      'MobileNo', 'CellNo', 'Mobile',
      'CLI'
    );
    const callerIdRaw = getFromRow(
      row,
      'callerId', 'CallerID', 'CallersID',
      'ContactID', 'contactId',
      'ID', 'id'
    );

    const phoneKey = onlyDigits(phone);
    const idKey = String(callerIdRaw || '').trim();
    return phoneKey && idKey ? `${phoneKey}|${idKey}` : '';
  }, [row]);

  // keep a ref of last loaded signature to avoid clearing state unnecessarily
  const lastLoadedSigRef = useRef('');

  useEffect(() => {
    setErr(''); // clear any old error on key change

    // nothing selected yet — keep current info as-is
    if (!signature) return;

    // If we already loaded this signature, do nothing
    if (lastLoadedSigRef.current === signature) return;

    // Try cache first
    const cached = readCache(signature);
    if (cached) {
      setInfo(cached);
      lastLoadedSigRef.current = signature;
      return;
    }

    // Not cached: fetch once
    const [phoneKey, idKey] = signature.split('|');
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE}/Bocallerinfo?cli=${encodeURIComponent(phoneKey)}&callerid=${encodeURIComponent(idKey)}`,
          { headers: { Accept: 'application/json' }, signal: ac.signal }
        );
        const json = await res.json();
        const first = Array.isArray(json?.data) ? json.data[0] : (json?.data ?? null);

        if (ac.signal.aborted) return;

        if (first) {
          writeCache(signature, first);
          setInfo(first);
          lastLoadedSigRef.current = signature;
        } else {
          setInfo(null);
          setErr('No caller record found.');
          lastLoadedSigRef.current = ''; // allow a future retry
        }
      } catch (e) {
        if (ac.signal.aborted) return;
        setErr(e?.message || 'Failed to load caller info');
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [signature]);

  if (!row) {
    return <div className="query-history-empty">Select a row to view caller information.</div>;
  }

  const cli = info?.CLI ?? info?.PhoneNo ?? getFromRow(row, 'contactNumber', 'ContactNumber', 'PhoneNo', 'MobileNo', 'CellNo', 'CLI');
  const name = info?.Name ?? getFromRow(row, 'contactPerson', 'ContactPerson', 'Name');

  return (
    <>
      {err && <div className="query-history-empty">{err}</div>}
      {loading && <div className="query-history-empty">Loading caller information…</div>}

      <div className="review-caller-grid">
        <label><span>CLI</span><input type="text" readOnly value={cli || ''} /></label>
        <label><span>Name</span><input type="text" readOnly value={name || ''} /></label>
        <label><span>CNIC</span><input type="text" readOnly value={info?.CNIC ?? ''} /></label>
        <label><span>Province</span><input type="text" readOnly value={info?.Province ?? ''} /></label>
        <label><span>Heard From</span><input type="text" readOnly value={info?.HeardFrom ?? ''} /></label>
        <label><span>Religion</span><input type="text" readOnly value={info?.Religion ?? ''} /></label>
        <label><span>District</span><input type="text" readOnly value={info?.District ?? ''} /></label>
        <label><span>Gender</span><input type="text" readOnly value={info?.Gender ?? ''} /></label>
        <label><span>Country</span><input type="text" readOnly value={info?.Country ?? ''} /></label>
        <label><span>City</span><input type="text" readOnly value={info?.City ?? ''} /></label>
        <label><span>Age</span><input type="text" readOnly value={info?.Age ?? ''} /></label>
        <label><span>Salary</span><input type="text" readOnly value={info?.Salary ?? ''} /></label>
        <label><span>Callback 2</span><input type="text" readOnly value={info?.CallbackTwo ?? ''} /></label>
        <label><span>Callback 1</span><input type="text" readOnly value={info?.CellNO ?? ''} /></label>
        <label><span>Father/Husb</span><input type="text" readOnly value={info?.FatherName ?? ''} /></label>
      </div>
      <div className="review-caller-notes">
        <label><span>Remarks</span><textarea rows="2" readOnly value={info?.Remarks ?? ''}></textarea></label>
        <label><span>Address</span><textarea rows="2" readOnly value={info?.Address ?? ''}></textarea></label>
      </div>
    </>
  );
}
